import { ApiEndpoints } from "@shared/api/api-endpoints";
import {
  bxApiClient,
  cxApiClient,
  onboardingApiClient,
} from "@shared/api/clients/apiClientFactory";
import { QueryStringParameters } from "@shared/api/url-builder";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { BillCycleDetail, BillCycleResponse, BillRunData, BillRunResponse, Choices, PaginatedBillCycleResponse, PaginatedOutstandingBillsResponse, PaginatedResponsePremise, SubArea } from "@features/bx/bill-cycle-v2/types";
import { PAGE_SIZE } from "@shared/utils/constants";
import { toSnakeCase } from "@shared/utils/caseConverter";
import { BillCorrectionResult } from "./types";

export const getChoices = async (choice: string, remoteUtilityId?: string): Promise<Choices[]> => {
  const url = ApiEndpoints.createUrlWithPathAndQueryVariables("bx", "choices", [
    choice,
  ],
  (qs: QueryStringParameters) => {
    if(remoteUtilityId) {
      qs.push('remote_utility_id', remoteUtilityId);
    }
  }
);
  const response = await bxApiClient.get(url);
  // Transform response.data.result from { key, value } to { label: key, value }
  const transformed = response.data.result;
  return transformed;
};

const isValidParam = (value: any) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

export const getOutstandingBills = async (
  queryParams: Record<string, any>,
  billCycleId: string | number
): Promise<PaginatedOutstandingBillsResponse> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();

  const url = ApiEndpoints.createUrlWithQueryParameters(
    "cx",
    "outstanding-bills",
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("bill_cycle_id", billCycleId.toString());
      qs.push("limit", PAGE_SIZE);

      Object.entries(queryParams).forEach(([key, value]) => {
        if (!isValidParam(value)) return;

        qs.push(toSnakeCase(key), value);
      });
    }
  );

  const response = await cxApiClient.get(url);
  return response.data;
};

export const getBillsFromSearch = async (
  queryParams: Record<string, any>,
): Promise<any> => {

  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    "get-outstanding-bills",
    (qs: QueryStringParameters) => {
      qs.push("limit", 5);
      qs.push("is_pagination", 1);
      Object.entries(queryParams).forEach(([key, value]) => {
        if (!isValidParam(value)) return;

        qs.push(toSnakeCase(key), value);
      });
    }
  );

  const response = await bxApiClient.get(url);
  return response.data;
};

export const getBillCorrectionTypes = async (): Promise<any> => {

  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    "correction-types",
  );

  const response = await bxApiClient.get(url);
  return response.data?.result;
};

export const initiateBillCorrection = async (payload) => {
  const url = ApiEndpoints.createUrl('bx', 'bill-correction/initiate');
  const response = await bxApiClient.post(url, payload)
  return response.data;
}

export const getBillCorrectionDetails = async (billId: number): Promise<any> => {

  const url = ApiEndpoints.createUrlWithPathVariables(
    "bx",
    "bill-correction-details",
    [billId],
  );

  const response = await bxApiClient.get(url);
  return response.data?.result;
};

export const patchBillCorrection = async <T>({
  id,
  type,
  payload,
}) => {
  const url = ApiEndpoints.createUrlWithPathVariables(
    'bx',
    `bill-correction`,
    [id, type]
  );

  const response = await bxApiClient.patch(url, payload);
  return response.data;
};

export const getCorrectedBillDetails = async (correctionId: number): Promise<BillCorrectionResult> => {

  const url = ApiEndpoints.createUrlWithPathVariables(
    "bx",
    "bill-correction",
    [correctionId, 'preview'],
  );

  const response = await bxApiClient.get(url);
  return response.data?.result;
};

export const getBillTemplates = async (remoteUtilityId: string): Promise<any> => {

  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    "bill-config-meta",
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", remoteUtilityId);
    },
  );

  const response = await bxApiClient.get(url);
  return response.data?.result;
};

export const postGenerateBill = async (correctionId: number): Promise<any> => {

  const url = ApiEndpoints.createUrlWithPathVariables(
    "bx",
    "bill-correction",
    [correctionId, 'generate-bill'],
  );

  const response = await bxApiClient.post(url);
  return response.data;
};

export const getBillPreview = async (correctionId: number): Promise<any> => {

  const url = ApiEndpoints.createUrlWithPathVariables(
    "bx",
    "bill-correction",
    [correctionId, 'download-corrected-bill'],
  );

  const response = await bxApiClient.get(url, {
    responseType: "blob"
  });
  return response.data;
};

export const postApproveBill = async (correctionId: number, payload: any): Promise<any> => {

  const url = ApiEndpoints.createUrlWithPathVariables(
    "bx",
    "bill-correction",
    [correctionId, 'update'],
  );

  const response = await bxApiClient.post(url, payload);
  return response.data;
};

export const getBillCorrectionList = async (remoteUtilityId: string, search: string, page: number): Promise<any> => {

  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    "bill-correction/list",
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", remoteUtilityId);
      if(search) qs.push("search_data", search);
      qs.push("page", page);
      qs.push('limit', PAGE_SIZE)
    },
  );

  const response = await bxApiClient.get(url);
  return response.data;
};
